import { useCallback, useEffect, useRef, useState, RefObject } from 'react';
import { notify } from '~/app/helpers/notify';
import useTranslation from '~/app/hooks/useTranslation';
import { useStoreActions, useStoreState } from '~/store';
import { DateRefObject } from '~/app/modules/form/Date';
import {
    isScheduledEndBeforeStart,
    scheduledDateApiToPicker,
    scheduledDatePickerToApi,
} from '~/workorder/Detail/MainContent/helper';
import { WorkOrderAutoSaveFieldKey, WorkOrderDetail } from '~/workorder/types';

type ScheduledDateFieldKey = Extract<
    WorkOrderAutoSaveFieldKey,
    'scheduledStartDate' | 'scheduledEndDate'
>;

export interface UseWorkOrderScheduledDateAutoSaveParams {
    workOrderId: WorkOrderDetail['id'];
    fieldKey: ScheduledDateFieldKey;
    savedValue: string;
    peerSavedValue: string;
    saving: boolean;
    setSaving: (saving: boolean) => void;
    dateRef: RefObject<DateRefObject>;
}

export const useWorkOrderScheduledDateAutoSave = ({
    workOrderId,
    fieldKey,
    savedValue,
    peerSavedValue,
    saving,
    setSaving,
    dateRef,
}: UseWorkOrderScheduledDateAutoSaveParams) => {
    const { t } = useTranslation('workorder');
    const { t: tAPIResponseMessages } = useTranslation('apiResponseMessages');
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const autoSaveWorkOrderData = useStoreActions(actions => actions.workOrder.autoSaveWorkOrderData);

    const savedPickerRef = useRef(scheduledDateApiToPicker(savedValue));
    const [pickerValue, setPickerValue] = useState(() => scheduledDateApiToPicker(savedValue));
    const peerPickerRef = useRef(scheduledDateApiToPicker(peerSavedValue));

    useEffect(() => {
        savedPickerRef.current = scheduledDateApiToPicker(savedValue);
        setPickerValue(savedPickerRef.current);
        dateRef.current?.setValue(savedPickerRef.current);
    }, [savedValue, dateRef]);

    useEffect(() => {
        peerPickerRef.current = scheduledDateApiToPicker(peerSavedValue);
    }, [peerSavedValue]);

    const revertToSaved = useCallback(() => {
        setPickerValue(savedPickerRef.current);
        dateRef.current?.setValue(savedPickerRef.current);
    }, [dateRef]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPickerValue(e.target.value);
    }, []);

    const handleBlur = useCallback(() => {
        if (saving) {
            return;
        }

        const nextPicker = pickerValue;
        if (nextPicker === savedPickerRef.current) {
            return;
        }

        const nextApi = scheduledDatePickerToApi(nextPicker);
        const savedApi = scheduledDatePickerToApi(savedPickerRef.current);

        if (!nextApi || nextApi === savedApi) {
            return;
        }

        const peerApi = scheduledDatePickerToApi(peerPickerRef.current);
        const startApi = fieldKey === 'scheduledStartDate' ? nextApi : peerApi;
        const endApi = fieldKey === 'scheduledEndDate' ? nextApi : peerApi;

        if (startApi && endApi && isScheduledEndBeforeStart(startApi, endApi)) {
            notify(t('error.scheduledEndBeforeStart'), 'error');
            revertToSaved();
            return;
        }

        if (netWorkStatus !== 0) {
            revertToSaved();
            return;
        }

        setSaving(true);
        autoSaveWorkOrderData({ key: fieldKey, workOrderId, value: nextApi })
            .then(response => {
                if (response.success) {
                    savedPickerRef.current = nextPicker;
                    notify(tAPIResponseMessages('changesSaved'), 'success');
                } else {
                    revertToSaved();
                }
            })
            .catch(() => {
                revertToSaved();
            })
            .finally(() => setSaving(false));
    }, [
        autoSaveWorkOrderData,
        fieldKey,
        netWorkStatus,
        pickerValue,
        revertToSaved,
        saving,
        setSaving,
        t,
        tAPIResponseMessages,
        workOrderId,
    ]);

    return {
        pickerValue,
        handleChange,
        handleBlur,
        revertToSaved,
    };
};
